import type { ReachEventStatus } from '@/services/data/reach/ReachEventService';

export const RESTORED_EVENT_STATUS: ReachEventStatus = 'planning';

export function isArchivedEvent(event: { status: ReachEventStatus }): boolean {
  return event.status === 'archived';
}

export function withoutArchivedEvents<T extends { status: ReachEventStatus }>(
  events: readonly T[],
): T[] {
  return events.filter((event) => !isArchivedEvent(event));
}

export interface EventArchiveEffects {
  update: (patch: { status: ReachEventStatus }) => Promise<unknown>;
  closePanel: () => void;
  notifyArchived: (undo: () => void) => void;
  notifyRestored: () => void;
  notifyFailed: (message: string, description: string) => void;
}

function describeError(err: unknown): string {
  return err instanceof Error ? err.message : '';
}

export function restoreArchivedEvent(
  effects: Pick<EventArchiveEffects, 'update' | 'notifyRestored' | 'notifyFailed'>,
  status: ReachEventStatus = RESTORED_EVENT_STATUS,
): Promise<void> {
  return effects.update({ status }).then(
    () => effects.notifyRestored(),
    (err: unknown) => effects.notifyFailed("Couldn't restore the event", describeError(err)),
  );
}

export function archiveEventWithUndo(
  previousStatus: ReachEventStatus,
  effects: EventArchiveEffects,
): Promise<void> {
  return effects.update({ status: 'archived' }).then(
    () => {
      effects.closePanel();
      effects.notifyArchived(() => {
        void restoreArchivedEvent(effects, previousStatus);
      });
    },
    (err: unknown) => effects.notifyFailed("Couldn't archive the event", describeError(err)),
  );
}
